import { useState } from "react";
import { useQueryClient } from "react-query";
import SyntaxHighlighter from "react-syntax-highlighter";
import { a11yDark } from "react-syntax-highlighter/dist/esm/styles/hljs";

import {
  DefaultButton,
  Dropdown,
  Heading,
  IconButton,
  IDropdownOption,
  IModalProps,
  MessageBar,
  MessageBarType,
  Modal,
  Stack,
  useTheme
} from "@bps/fluent-ui";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import {
  FieldConfigurationVersion,
  FieldDevice,
  FieldDeviceConfiguration
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  FieldOpsCacheKeys,
  useFieldDeviceConfiguration
} from "@libs/api/gateways/field/field-ops-gateway.hooks";

interface DesiredConfigModalProps extends IModalProps {
  show: boolean;
  device: FieldDevice;
  onDismiss: () => void;
}

export const DesiredConfigModal: React.FC<DesiredConfigModalProps> = ({
  device,
  show,
  onDismiss,
  ...props
}) => {
  const [configurationVersion, setConfigurationVersion] = useState<string>(
    FieldConfigurationVersion.V3_0
  );

  const fieldDeviceDesiredConfigQuery = useFieldDeviceConfiguration(
    device.id,
    configurationVersion
  );

  const queryClient = useQueryClient();
  const invalidateQueryCache = async () => {
    await queryClient.invalidateQueries([
      FieldOpsCacheKeys.FieldDeviceDesiredConfig,
      device.id,
      configurationVersion
    ]);
  };

  const copyToClipboard = async () => {
    const configurationResult = queryClient.getQueryData([
      FieldOpsCacheKeys.FieldDeviceDesiredConfig,
      device.id,
      configurationVersion
    ]);

    await navigator.clipboard.writeText(
      JSON.stringify(configurationResult, null, 2)
    );
  };

  const configurationVersionOptions: IDropdownOption[] = Object.keys(
    FieldConfigurationVersion
  ).map(key => ({
    key: FieldConfigurationVersion[key],
    text: `Field Configuration v${FieldConfigurationVersion[key]}`
  }));

  const theme = useTheme();
  return (
    <Modal
      isOpen={show}
      {...props}
      styles={{ scrollableContent: { maxHeight: "none" } }}
    >
      <Stack
        verticalFill
        styles={{
          root: { padding: theme.spacing.l1, maxHeight: 800 }
        }}
        tokens={{ childrenGap: theme.spacing.s1 }}
      >
        <Stack horizontal horizontalAlign="space-between">
          <Heading variant="modal-heading">
            Desired Field Configuration Preview
          </Heading>
          <IconButton onClick={onDismiss} iconProps={{ iconName: "Cancel" }} />
        </Stack>

        <MessageBar messageBarType={MessageBarType.info}>
          The download and install start dates have been adjusted using the
          DownloadAndInstallRatio device setting.
        </MessageBar>

        <Stack
          verticalFill
          styles={{
            root: { maxHeight: 600, overflow: "auto", maxWidth: 1200 }
          }}
        >
          <QueryStateIndicator<FieldDeviceConfiguration>
            {...fieldDeviceDesiredConfigQuery}
          >
            {json => (
              <SyntaxHighlighter
                language="json"
                // eslint-disable-next-line react/forbid-component-props
                style={a11yDark}
                wrapLongLines
                wrapLines
                customStyle={{
                  wordBreak: "break-word"
                }}
              >
                {JSON.stringify(json, null, 2)}
              </SyntaxHighlighter>
            )}
          </QueryStateIndicator>
        </Stack>

        <Stack
          horizontal
          tokens={{ childrenGap: theme.spacing.s1 }}
          horizontalAlign="end"
        >
          <Dropdown
            name="configuration-version-dropdown"
            onChange={(_e, options) =>
              setConfigurationVersion(String(options.option?.key))
            }
            options={configurationVersionOptions}
            selectedKey={configurationVersion}
            defaultSelectedKey={FieldConfigurationVersion.V2_0}
            withNoEmptyOption
          />
          <DefaultButton onClick={copyToClipboard}>
            Copy to Clipboard
          </DefaultButton>
          <DefaultButton onClick={invalidateQueryCache}>Reload</DefaultButton>
          <DefaultButton onClick={onDismiss}>Cancel</DefaultButton>
        </Stack>
      </Stack>
    </Modal>
  );
};
